// Typed quantity for a Fashion cart line. Applies the same gate as Core's
// handleTotalQty (no returns, no restrictPosQtyEdit lines) and also refuses once
// a payment is on the bill, because the tendered amount would go stale.

export function lineQtyBlockedReason(line, cart) {
  if (!line) return "Select a line first.";
  if (line.isReturn) return "Return lines keep the returned quantity.";
  if (line.restrictPosQtyEdit === "Y") return "Quantity can't be edited for this item.";
  if ((cart?.payments || []).length > 0) return "Remove the payments to change quantities.";
  return null;
}

// Returns { qty } on success or { error } with a message for the cashier.
export function parseLineQty(raw, line) {
  const text = String(raw ?? "").trim();
  if (!/^\d*\.?\d+$|^\d+\.$/.test(text)) return { error: "Enter a quantity greater than 0." };
  const value = Number(text);
  if (!(value > 0)) return { error: "Enter a quantity greater than 0." };
  if (line?.isDecimalQty !== true && !Number.isInteger(value)) return { error: "This item is sold in whole units." };
  const precision = Number.isInteger(line?.isQtyDesimal) ? line.isQtyDesimal : 2;
  return { qty: parseFloat(value.toFixed(precision)) };
}

// Header count for the bill. Return lines carry a negative quantity, so a plain
// sum shows "-1 items" on an exchange; count the pieces going out and coming
// back separately instead.
export function piecesLabel(items) {
  let out = 0;
  let back = 0;
  for (const i of items || []) {
    const q = Number(i?.weight) || 0;
    if (i?.isReturn || q < 0) back += Math.abs(q);
    else out += q;
  }
  const fmt = (n) => String(parseFloat(n.toFixed(3)));
  const pieces = (n) => `${fmt(n)} ${n === 1 ? "piece" : "pieces"}`;
  if (back === 0) return pieces(out);
  if (out === 0) return `${fmt(back)} returning`;
  return `${pieces(out)} · ${fmt(back)} returning`;
}

// Bag line title. Variant names often end with their colour and size ("Flannel
// Shirt Green M") and the line already shows "Green · M" underneath, so drop
// those trailing words. Keeps the full name if nothing would be left.
export function lineTitle(name, variant) {
  const full = String(name ?? "").trim();
  const parts = String(variant ?? "")
    .split("·")
    .map((p) => p.trim().toLowerCase())
    .filter(Boolean);
  if (!full || parts.length === 0) return full;
  const words = full.split(/\s+/);
  let end = words.length;
  for (let i = parts.length - 1; i >= 0; i--) {
    const n = parts[i].split(/\s+/).length;
    if (end - n < 1) break;
    const tail = words.slice(end - n, end).join(" ").toLowerCase();
    if (tail !== parts[i]) break;
    end -= n;
  }
  return words.slice(0, end).join(" ").replace(/[\s,–—-]+$/, "") || full;
}
